import { Stack } from "@mui/material";
import { useAtomValue } from "jotai";
import { useCallback, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { isStudioProfileAtom } from "../../../atoms/user/activeProfileAtom";
import { TRACKING_EVENTS_NAME } from "../../../constants/trackingEvents";
import { useGetSubscriptionStatus } from "../../../hooks/useGetSubscriptionStatus";
import {
  useEngineerSubscriptionBenefits,
  useStudioSubscriptionBenefits,
  useSubscriptionName,
  useSubscriptionTrophy,
} from "../../../hooks/useSubscriptionTrophy";
import { AccountType } from "../../../store/actions/dashboard";
import { getStripePortalSession } from "../../../store/actions/subscriptions";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_STATUS,
} from "../../../store/models/subscription";
import { PennyDollarFormatter } from "../../../store/utils/formatUtils";
import { emitAnalyticsTrackingEvent } from "../../../utils/analyticsUtils";
import { Button, ButtonVariant } from "../../core-ui/components/Button/Button";
import { Text, TEXT_WEIGHT } from "../../core-ui/components/Text/Text";
import {
  TextColor,
  TextStyleVariant,
} from "../../core-ui/components/Text/TextUtils";
import {
  ManageSubscriptionCardBenefitList,
  ManageSubscriptionCardBenefitListItem,
  ManageSubscriptionCardHeader,
} from "./ManageSubscriptionCard.styles";

interface ManageSubscriptionSectionProps {
  nonPlatinumSubscriptionPlans: SUBSCRIPTION_PLAN[];
  subscriptionRoute: string;
  onClose?: () => void;
  trackingEventName: TRACKING_EVENTS_NAME;
  accountType: AccountType;
  feesAvoided?: number;
  currency?: string;
}

export const ManageSubscriptionSection = ({
  nonPlatinumSubscriptionPlans,
  subscriptionRoute,
  onClose,
  trackingEventName,
  accountType,
  feesAvoided,
  currency,
}: ManageSubscriptionSectionProps) => {
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector((state) => state.accountInfo.user);
  const isStudioProfileActive = useAtomValue(isStudioProfileAtom);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const navigate = useNavigate();

  const { data: subscription, isFetching: isLoadingSubscription } =
    useGetSubscriptionStatus({
      enabled: true,
      refetchSubsStatus: true,
      accountType,
      user: loggedInUser,
    });

  const subscriptionPlanChoice = useMemo(() => {
    return (
      subscription?.subscription_plan_choice ||
      SUBSCRIPTION_PLAN.NO_SUBSCRIPTION_PLAN
    );
  }, [subscription?.subscription_plan_choice]);

  const subscriptionStatus = useMemo(() => {
    return (
      subscription?.stripe_subscription_status ||
      SUBSCRIPTION_STATUS.NOT_DEFINED
    );
  }, [subscription?.stripe_subscription_status]);

  const trophyAsset = useSubscriptionTrophy(subscriptionPlanChoice);
  const userBenefits = useEngineerSubscriptionBenefits(subscriptionPlanChoice);
  const studioBenefits = useStudioSubscriptionBenefits(subscriptionPlanChoice);
  const benefits =
    isStudioProfileActive && accountType !== AccountType.ARTIST
      ? studioBenefits
      : userBenefits;
  const subscriptionName = useSubscriptionName(subscriptionPlanChoice);

  const hasAnInactiveSubscription = useMemo(() => {
    return (
      !nonPlatinumSubscriptionPlans.includes(subscriptionPlanChoice) &&
      subscriptionStatus === SUBSCRIPTION_STATUS.INACTIVE
    );
  }, [
    nonPlatinumSubscriptionPlans,
    subscriptionPlanChoice,
    subscriptionStatus,
  ]);

  const handleOnClick = useCallback(() => {
    emitAnalyticsTrackingEvent(trackingEventName, {
      user_id: loggedInUser?.id,
    });

    if (nonPlatinumSubscriptionPlans.includes(subscriptionPlanChoice)) {
      navigate(subscriptionRoute);
      onClose?.();
      return;
    }

    setLoadingPortal(true);
    void dispatch(getStripePortalSession())
      .unwrap()
      .then(({ portal_url }) => {
        window.location.href = portal_url;
      })
      .catch(() => {
        setLoadingPortal(false);
      });
  }, [
    dispatch,
    loggedInUser?.id,
    navigate,
    nonPlatinumSubscriptionPlans,
    onClose,
    subscriptionPlanChoice,
    subscriptionRoute,
    trackingEventName,
  ]);

  return (
    <Stack spacing={2}>
      <ManageSubscriptionCardHeader>
        <img
          alt={`${accountType} subscription trophy`}
          width={30}
          height={30}
          src={trophyAsset}
          style={{ marginRight: "8px" }}
        />
        {isLoadingSubscription ? (
          <Skeleton width={150} count={1} />
        ) : (
          <Text variant={TextStyleVariant.P2} weight={TEXT_WEIGHT.SEMI_BOLD}>
            {subscriptionName}
          </Text>
        )}
      </ManageSubscriptionCardHeader>
      {hasAnInactiveSubscription && (
        <Text weight={TEXT_WEIGHT.SEMI_BOLD}>
          Your <strong>{subscriptionName}</strong> is inactive. Click below to
          update your subscription
        </Text>
      )}
      <ManageSubscriptionCardBenefitList>
        {benefits?.map((benefit, index) => {
          if (isLoadingSubscription) {
            return (
              <ManageSubscriptionCardBenefitListItem
                key={`${benefit}-${index}`}
              >
                <Skeleton count={1} />
              </ManageSubscriptionCardBenefitListItem>
            );
          }
          return (
            <ManageSubscriptionCardBenefitListItem key={`${benefit}-${index}`}>
              {benefit}
            </ManageSubscriptionCardBenefitListItem>
          );
        })}
      </ManageSubscriptionCardBenefitList>
      {subscription &&
        !nonPlatinumSubscriptionPlans.includes(
          subscription.subscription_plan_choice,
        ) && (
          <Text weight={TEXT_WEIGHT.SEMI_BOLD}>
            Some benefits unlocked after free trial
          </Text>
        )}

      {feesAvoided && (
        <Text
          color={TextColor.SUCCESS}
        >{`So far you've saved ${PennyDollarFormatter(currency).format(
          feesAvoided,
        )} in fees because of your membership!`}</Text>
      )}

      <Button
        variant={ButtonVariant.OUTLINED}
        disabled={isLoadingSubscription}
        onClick={handleOnClick}
        loading={loadingPortal}
        fullWidth
      >
        Manage{" "}
        {accountType === AccountType.ARTIST ? "Artist" : "Service Provider"}{" "}
        Subscription
      </Button>
    </Stack>
  );
};
